import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LinkIcon from '@mui/icons-material/Link'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import BlockIcon from '@mui/icons-material/Block'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import { AWAITING_WITNESS_STATUS } from '../incidentConstants'

// Reviewer-side management of external witness statement links. A link is
// only minted after the reviewer ticks every data-protection acknowledgement
// (the wording comes from the server so what was accepted is what gets
// stored). The link is shown once on creation and again for as long as the
// request is open.

const EXPIRY_OPTIONS = [
  { days: 7, label: '7 days' },
  { days: 14, label: '14 days (recommended)' },
  { days: 30, label: '30 days' },
]

const STATE_CHIP = {
  open: { label: 'Awaiting statement', color: 'warning' },
  used: { label: 'Statement received', color: 'success' },
  expired: { label: 'Expired', color: 'default' },
  revoked: { label: 'Revoked', color: 'error' },
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (e) {
    return false
  }
}

const isEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? '').trim())

const LinkField = ({ link, onCopied }) => (
  <TextField
    value={link}
    fullWidth
    size="small"
    label="Statement link"
    InputProps={{
      readOnly: true,
      startAdornment: (
        <InputAdornment position="start">
          <LinkIcon fontSize="small" />
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end">
          <Tooltip title="Copy link">
            <IconButton
              size="small"
              onClick={async () => {
                const ok = await copyToClipboard(link)
                onCopied?.(ok)
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      ),
    }}
    onFocus={(e) => e.target.select()}
  />
)

const RequestDialog = ({ open, onClose, incident, config, onCreated }) => {
  const { setToast } = useContext(GlobalContext)
  const [witnessName, setWitnessName] = useState('')
  const [witnessEmail, setWitnessEmail] = useState('')
  const [witnessInvolvement, setWitnessInvolvement] = useState('')
  const [brief, setBrief] = useState('')
  const [expiresInDays, setExpiresInDays] = useState(config?.defaultDays ?? 14)
  const [sendEmail, setSendEmail] = useState(true)
  const [setAwaitingStatus, setSetAwaitingStatus] = useState(true)
  const [ticked, setTicked] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [created, setCreated] = useState(null)

  useEffect(() => {
    if (!open) return
    setWitnessName('')
    setWitnessEmail('')
    setWitnessInvolvement('')
    setBrief('')
    setExpiresInDays(config?.defaultDays ?? 14)
    setSendEmail(true)
    setSetAwaitingStatus(incident?.status !== AWAITING_WITNESS_STATUS)
    setTicked([])
    setSaving(false)
    setError(null)
    setCreated(null)
  }, [open])

  const statements = config?.acknowledgement?.statements ?? []
  const allTicked =
    statements.length > 0 &&
    statements.every((_, index) => ticked.includes(index))
  const emailOk = !witnessEmail.trim() || isEmail(witnessEmail)
  const canSubmit =
    witnessName.trim().length > 0 &&
    emailOk &&
    (!sendEmail || witnessEmail.trim().length > 0) &&
    allTicked &&
    !saving

  const toggle = (index) =>
    setTicked((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    )

  const submit = () => {
    setSaving(true)
    setError(null)
    API.post(`/incident/${incident._id}/witness-requests`, {
      witnessName: witnessName.trim(),
      witnessEmail: witnessEmail.trim(),
      witnessInvolvement: witnessInvolvement.trim(),
      brief: brief.trim(),
      expiresInDays,
      sendEmail: sendEmail && witnessEmail.trim().length > 0,
      setAwaitingStatus,
      acknowledgementAccepted: true,
      acknowledgementVersion: config?.acknowledgement?.version,
    })
      .then((res) => {
        if (res.status === 200) {
          setCreated(res.data)
          onCreated?.(res.data)
          if (
            res.data?.email &&
            !res.data.email.success &&
            !res.data.email.muzzled
          ) {
            setToast({
              message:
                'Link created, but the email could not be sent — copy the link and send it yourself.',
            })
          }
        } else {
          setError(
            res.data?.message ||
              res.data?.error ||
              'Unable to create the link. Please try again.',
          )
        }
      })
      .catch(() => setError('Unable to create the link. Please try again.'))
      .finally(() => setSaving(false))
  }

  const expiryDate = moment().add(expiresInDays, 'days')

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        {created
          ? 'Statement link created'
          : 'Request an external witness statement'}
      </DialogTitle>
      <DialogContent dividers>
        {created ? (
          <Stack spacing={2}>
            <Alert
              severity="success"
              icon={<VerifiedUserOutlinedIcon fontSize="inherit" />}
            >
              <AlertTitle>
                Link ready for {created.request?.witnessName}
              </AlertTitle>
              {created.email?.success
                ? created.email.muzzled
                  ? 'Emails are switched off on this demo organisation — copy the link below to share it.'
                  : `The link has been emailed to ${created.request?.witnessEmail}. You can also copy it below.`
                : 'Copy the link below and send it to the witness through a channel you trust.'}{' '}
              It expires on{' '}
              <strong>
                {moment(created.request?.expires).format(
                  'Do MMM YYYY [at] HH:mm',
                )}
              </strong>{' '}
              and can be used once.
            </Alert>
            <LinkField
              link={created.request?.link ?? ''}
              onCopied={(ok) =>
                setToast({
                  message: ok
                    ? 'Link copied to clipboard.'
                    : 'Unable to copy — select the link and copy it manually.',
                })
              }
            />
            {created.incidentStatus === AWAITING_WITNESS_STATUS && (
              <Typography variant="body2" color="text.secondary">
                The incident is now marked <strong>Awaiting Witness</strong>. It
                returns to its previous status automatically when the statement
                arrives, or you can change it yourself at any time.
              </Typography>
            )}
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <Alert severity="info" icon={false}>
              The witness receives a private, single-use link that opens a
              statement form without a login. They will see the incident number
              and date, the brief you write below, and a privacy notice naming
              your organisation — nothing else from the incident record.
            </Alert>

            {error && <Alert severity="error">{error}</Alert>}
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Witness name"
                    value={witnessName}
                    onChange={(e) => setWitnessName(e.target.value)}
                    required
                    fullWidth
                    size="small"
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Witness email"
                    value={witnessEmail}
                    onChange={(e) => setWitnessEmail(e.target.value)}
                    fullWidth
                    size="small"
                    autoComplete="off"
                    error={!emailOk}
                    helperText={
                      !emailOk
                        ? 'Enter a valid email address'
                        : 'Optional — needed if you want AmbuNet to email the link'
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Their involvement"
                    placeholder="e.g. Event steward on the gate"
                    value={witnessInvolvement}
                    onChange={(e) => setWitnessInvolvement(e.target.value)}
                    fullWidth
                    size="small"
                    autoComplete="off"
                    helperText="Pre-fills the form for them; they can change it"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Link expires after"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(Number(e.target.value))}
                    fullWidth
                    size="small"
                    helperText={`Expires ${expiryDate.format('Do MMM YYYY')}`}
                  >
                    {EXPIRY_OPTIONS.filter(
                      (option) => option.days <= (config?.maxDays ?? 30),
                    ).map((option) => (
                      <MenuItem key={option.days} value={option.days}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Brief for the witness"
                    placeholder="What would you like them to describe? e.g. What you saw between 14:00 and 14:30 near the medical tent."
                    value={brief}
                    onChange={(e) => setBrief(e.target.value.slice(0, 1000))}
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    helperText={`${brief.length}/1000 — shown to the witness. Do not include names or details of patients, staff or anyone else.`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sendEmail && witnessEmail.trim().length > 0}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        disabled={witnessEmail.trim().length === 0}
                        color="success"
                      />
                    }
                    label="Email the link to the witness now"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={setAwaitingStatus}
                        onChange={(e) => setSetAwaitingStatus(e.target.checked)}
                        disabled={incident?.status === AWAITING_WITNESS_STATUS}
                        color="success"
                      />
                    }
                    label={
                      incident?.status === AWAITING_WITNESS_STATUS
                        ? 'Incident is already Awaiting Witness'
                        : 'Mark incident as Awaiting Witness'
                    }
                  />
                </Grid>
              </Grid>
            </Box>
            <Divider />

            <Box>
              <Typography fontWeight={800} gutterBottom>
                Data protection acknowledgement
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                You are sharing information about this incident outside the
                organisation and collecting personal data from the witness.
                Confirm each statement to create the link — your confirmation is
                recorded on the incident's audit history.
              </Typography>
              <Stack spacing={0.5} mt={1}>
                {statements.map((statement, index) => (
                  <FormControlLabel
                    key={index}
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                    control={
                      <Checkbox
                        checked={ticked.includes(index)}
                        onChange={() => toggle(index)}
                        sx={{ pt: 0.25 }}
                      />
                    }
                    label={<Typography variant="body2">{statement}</Typography>}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {created ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} color="inherit" disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={submit}
              disabled={!canSubmit}
              startIcon={<LinkIcon />}
            >
              {saving ? 'Creating…' : 'Create link'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

const RequestCard = ({ request, onResend, onRevoke, onCopy }) => {
  const chip = STATE_CHIP[request.state] ?? STATE_CHIP.expired
  const createdBy = request.createdBy
    ? `${request.createdBy.firstName ?? ''} ${request.createdBy.lastName ?? ''}`.trim()
    : null
  return (
    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
              {(request.witnessName ?? '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box minWidth={0}>
              <Typography fontWeight={700} noWrap>
                {request.witnessName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {[request.witnessInvolvement, request.witnessEmail]
                  .filter(Boolean)
                  .join(' · ') || 'No contact details held'}
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={chip.label}
            color={chip.color}
            sx={{ borderRadius: 1, fontWeight: 700 }}
          />
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {request.state === 'used' && request.completedAt
            ? `Statement received ${moment(request.completedAt).format('Do MMM YYYY [at] HH:mm')}`
            : request.state === 'revoked'
              ? `Revoked ${moment(request.revokedAt).format('Do MMM YYYY [at] HH:mm')}${
                  request.revokedReason ? ` — ${request.revokedReason}` : ''
                }`
              : request.state === 'expired'
                ? `Expired ${moment(request.expires).format('Do MMM YYYY [at] HH:mm')} without a statement`
                : `Expires ${moment(request.expires).format('Do MMM YYYY [at] HH:mm')} (${moment(
                    request.expires,
                  ).fromNow()})`}
          {' · '}
          Created {moment(request.createdAt).format('Do MMM YYYY')}
          {createdBy && ` by ${createdBy}`}
          {request.emailSentAt &&
            ` · Emailed ${moment(request.emailSentAt).format('Do MMM HH:mm')}${
              request.emailSendCount > 1 ? ` (×${request.emailSendCount})` : ''
            }`}
          {request.openedCount > 0 &&
            ` · Opened ${request.openedCount} time${request.openedCount === 1 ? '' : 's'}`}
        </Typography>

        {request.state === 'open' && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => onCopy(request)}
            >
              Copy link
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<MailOutlineIcon />}
              onClick={() => onResend(request)}
            >
              {request.emailSentAt ? 'Re-send email' : 'Send by email'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => onRevoke(request)}
            >
              Revoke
            </Button>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}

const ResendDialog = ({ request, onClose, onSend }) => {
  const [email, setEmail] = useState(request?.witnessEmail ?? '')
  const [extend, setExtend] = useState(false)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    setEmail(request?.witnessEmail ?? '')
    setExtend(false)
    setBusy(false)
  }, [request])
  return (
    <Dialog
      open={!!request}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Email the link</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Witness email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
            error={email.length > 0 && !isEmail(email)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={extend}
                onChange={(e) => setExtend(e.target.checked)}
              />
            }
            label="Extend expiry to 14 days from now"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isEmail(email) || busy}
          onClick={async () => {
            setBusy(true)
            await onSend({
              witnessEmail: email.trim(),
              extendDays: extend ? 14 : undefined,
            })
            setBusy(false)
          }}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const RevokeDialog = ({ request, onClose, onRevoke }) => {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    setReason('')
    setBusy(false)
  }, [request])
  return (
    <Dialog
      open={!!request}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Revoke this link?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            The link sent to <strong>{request?.witnessName}</strong> will stop
            working immediately. You can issue a new one afterwards.
          </Typography>
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 500))}
            fullWidth
            size="small"
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={busy}>
          Keep link
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            await onRevoke({ reason: reason.trim() })
            setBusy(false)
          }}
        >
          Revoke
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const ExternalWitnessRequests = ({
  incident,
  setIncident,
  onStatementsChanged,
}) => {
  const { setToast } = useContext(GlobalContext)
  const [config, setConfig] = useState(null)
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [resending, setResending] = useState(null)
  const [revoking, setRevoking] = useState(null)

  const load = () => {
    setLoading(true)
    API.get(`/incident/${incident._id}/witness-requests`)
      .then((res) => {
        if (res.status === 200) {
          setConfig({
            acknowledgement: res.data.acknowledgement,
            defaultDays: res.data.defaultDays,
            maxDays: res.data.maxDays,
          })
          setRequests(res.data.requests ?? [])
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (incident?._id) load()
  }, [incident?._id])

  const applyStatus = (status) => {
    if (status && status !== incident.status) {
      setIncident((prev) => ({ ...prev, status }))
    }
  }

  const handleCreated = (data) => {
    setRequests((prev) => [data.request, ...prev])
    applyStatus(data.incidentStatus)
  }

  const handleCopy = async (request) => {
    const ok = await copyToClipboard(request.link)
    setToast({
      message: ok
        ? 'Link copied to clipboard.'
        : 'Unable to copy automatically — use the Copy button in the link field.',
    })
  }

  const handleResend = async (payload) => {
    const res = await API.post(
      `/incident/${incident._id}/witness-requests/${resending._id}/resend`,
      payload,
    )
    if (res.status === 200) {
      setRequests((prev) =>
        prev.map((r) =>
          r._id === res.data.request._id ? res.data.request : r,
        ),
      )
      setToast({
        message: res.data.email?.success
          ? res.data.email.muzzled
            ? 'Emails are switched off on this demo organisation.'
            : 'Link emailed to the witness.'
          : 'The email could not be sent — copy the link and send it yourself.',
      })
      setResending(null)
    } else {
      setToast({ message: res.data?.message || 'Unable to send the email.' })
    }
  }

  const handleRevoke = async (payload) => {
    const res = await API.post(
      `/incident/${incident._id}/witness-requests/${revoking._id}/revoke`,
      payload,
    )
    if (res.status === 200) {
      setRequests((prev) =>
        prev.map((r) =>
          r._id === res.data.request._id ? res.data.request : r,
        ),
      )
      applyStatus(res.data.incidentStatus)
      setToast({ message: 'Link revoked.' })
      setRevoking(null)
    } else {
      setToast({ message: res.data?.message || 'Unable to revoke the link.' })
    }
  }

  const awaiting = requests.filter((r) => r.state === 'open').length

  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight={800}>External witness links</Typography>
              {awaiting > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={`${awaiting} awaiting`}
                  sx={{ borderRadius: 1, fontWeight: 700 }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Invite someone outside the organisation to give their statement
              through a private, expiring link. Statements arrive in the list
              below and you are notified.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddAltIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={!config}
            sx={{ flexShrink: 0 }}
          >
            Request a statement
          </Button>
        </Stack>

        {!loading && requests.length === 0 && (
          <Typography variant="body2" color="text.disabled">
            No external statement links have been issued for this incident.
          </Typography>
        )}

        {requests.length > 0 && (
          <Stack spacing={1}>
            {requests.map((request) => (
              <RequestCard
                key={request._id}
                request={request}
                onCopy={handleCopy}
                onResend={setResending}
                onRevoke={setRevoking}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <RequestDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false)
          onStatementsChanged?.()
        }}
        incident={incident}
        config={config}
        onCreated={handleCreated}
      />
      <ResendDialog
        request={resending}
        onClose={() => setResending(null)}
        onSend={handleResend}
      />
      <RevokeDialog
        request={revoking}
        onClose={() => setRevoking(null)}
        onRevoke={handleRevoke}
      />
    </Card>
  )
}

export default ExternalWitnessRequests
